
import { useState, FormEvent } from 'react';

export default function RegistrationForm() {
  const [formData, setFormData] = useState({
    name: '',
    jobType: '',
    experience: '',
    age: '',
    company1: '',
    company2: '',
    email: '',
    emailConfirm: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setEmailError('');

    if (formData.email !== formData.emailConfirm) {
      setEmailError('メールアドレスが一致しません。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('氏名', formData.name);
      formBody.append('希望職種', formData.jobType);
      formBody.append('経験年数', formData.experience);
      formBody.append('年齢', formData.age);
      formBody.append('過去所属企業名①', formData.company1);
      formBody.append('過去所属企業名②', formData.company2);
      formBody.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d6irgomfjdhm2lq4sv6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', jobType: '', experience: '', age: '', company1: '', company2: '', email: '', emailConfirm: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="registration" className="py-12 bg-[#0d1b2e]">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-white">
          無料登録
        </h2>
        <form
          id="registration-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto bg-[#112240] p-6 rounded-lg space-y-4 border border-slate-700"
        >
          {/* 氏名 */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1 text-slate-300">
              氏名 <span className="text-amber-400">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white"
              placeholder="山田 太郎"
            />
          </div>

          {/* 希望職種・経験年数 */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="jobType" className="block text-sm font-medium mb-1 text-slate-300">
                希望職種 <span className="text-amber-400">*</span>
              </label>
              <select
                id="jobType"
                name="jobType"
                required
                value={formData.jobType}
                onChange={(e) => setFormData({ ...formData, jobType: e.target.value })}
                className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="コンサルタント">コンサルタント</option>
                <option value="エンジニア">エンジニア</option>
              </select>
            </div>
            <div>
              <label htmlFor="experience" className="block text-sm font-medium mb-1 text-slate-300">
                経験年数 <span className="text-amber-400">*</span>
              </label>
              <select
                id="experience"
                name="experience"
                required
                value={formData.experience}
                onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="0～5年">0～5年</option>
                <option value="5～10年">5～10年</option>
                <option value="10年以上">10年以上</option>
              </select>
            </div>
          </div>

          {/* 年齢 */}
          <div>
            <label htmlFor="age" className="block text-sm font-medium mb-1 text-slate-300">
              年齢 <span className="text-amber-400">*</span>
            </label>
            <input
              type="number"
              id="age"
              name="age"
              required
              min={18}
              max={99}
              value={formData.age}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setFormData({ ...formData, age: val });
              }}
              onKeyDown={(e) => {
                const allowed = ['Backspace','Delete','Tab','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Enter'];
                if (!allowed.includes(e.key) && !/^[0-9]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData('text');
                if (!/^\d+$/.test(pasted)) {
                  e.preventDefault();
                }
              }}
              className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white"
              placeholder="例：35"
            />
          </div>

          {/* 過去所属企業名 */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="company1" className="block text-sm font-medium mb-1 text-slate-300">
                過去所属企業名①
                <span className="text-slate-500 text-xs ml-1">（任意）</span>
              </label>
              <input
                type="text"
                id="company1"
                name="company1"
                value={formData.company1}
                onChange={(e) => setFormData({ ...formData, company1: e.target.value })}
                className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white"
                placeholder="例：株式会社〇〇"
              />
            </div>
            <div>
              <label htmlFor="company2" className="block text-sm font-medium mb-1 text-slate-300">
                過去所属企業名②
                <span className="text-slate-500 text-xs ml-1">（任意）</span>
              </label>
              <input
                type="text"
                id="company2"
                name="company2"
                value={formData.company2}
                onChange={(e) => setFormData({ ...formData, company2: e.target.value })}
                className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white"
                placeholder="例：株式会社△△"
              />
            </div>
          </div>

          {/* メールアドレス */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1 text-slate-300">
                メールアドレス <span className="text-amber-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white"
                placeholder="example@email.com"
              />
            </div>
            <div>
              <label htmlFor="emailConfirm" className="block text-sm font-medium mb-1 text-slate-300">
                メールアドレス（再入力） <span className="text-amber-400">*</span>
              </label>
              <input
                type="email"
                id="emailConfirm"
                name="emailConfirm"
                required
                value={formData.emailConfirm}
                onChange={(e) => setFormData({ ...formData, emailConfirm: e.target.value })}
                className="w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm bg-[#0d1b2e] text-white"
                placeholder="example@email.com"
              />
            </div>
          </div>

          {emailError && (
            <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
              {emailError}
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="bg-green-900/30 border border-green-500 text-green-300 px-4 py-3 rounded-lg text-sm space-y-1">
              <p>ご登録ありがとうございます。</p>
              <p>プロジェクトマスター運営事務局です。</p>
              <p>以後案件を配信致します。</p>
              <p>どうぞ宜しくお願い申し上げます。</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-3 rounded-lg text-sm">
              送信に失敗しました。もう一度お試しください。
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-amber-500 text-[#0a1628] font-bold py-3 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? '送信中...' : '無料登録する'}
          </button>
        </form>
      </div>
    </section>
  );
}
